'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { X, ZoomIn, Globe, Headphones, Phone, Megaphone, Grid3X3, ExternalLink } from 'lucide-react';

/* ─── Types ─────────────────────────────────────────────── */
interface GalleryItem {
  id: string;
  title: string;
  subtitle: string;
  category: 'it' | 'bpo' | 'voip' | 'marketing' | 'team' | 'office';
  image: string;
  alt: string;
  span?: 'wide' | 'tall' | 'large';
  tags: string[];
}

/* ─── Data ───────────────────────────────────────────────── */
const galleryItems: GalleryItem[] = [
{
  id: 'g1',
  title: 'Web Platform Launch',
  subtitle: 'Full-stack e-commerce solution for NovaCorp',
  category: 'it',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5276c20-1772274074387.png",
  alt: 'Software developer writing code on multiple monitors in a modern tech office',
  span: 'wide',
  tags: ['React', 'Next.js', 'E-Commerce']
},
{
  id: 'g2',
  title: 'BPO Operations Center',
  subtitle: 'State-of-the-art call center facility in Dubai',
  category: 'bpo',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17e40c08a-1772174200600.png",
  alt: 'Professional call center agents wearing headsets working at their desks in a modern BPO office',
  span: 'tall',
  tags: ['BPO', '24/7 Support']
},
{
  id: 'g3',
  title: 'VOIP Infrastructure',
  subtitle: 'Enterprise-grade SIP telephony rollout',
  category: 'voip',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11e91fd37-1771243342301.png",
  alt: 'Modern VOIP phone system and communication technology equipment on a desk',
  tags: ['SIP', 'HD Voice']
},
{
  id: 'g4',
  title: 'Digital Marketing Campaign',
  subtitle: 'Multi-channel campaign for Elevate Digital — 3× organic growth',
  category: 'marketing',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_108dd66c2-1772295872863.png",
  alt: 'Digital marketing analytics dashboard showing charts and social media campaign performance metrics',
  tags: ['SEO', 'PPC', 'Social']
},
{
  id: 'g5',
  title: 'Mobile App — FinTrack',
  subtitle: 'Cross-platform finance tracker for 50k+ users',
  category: 'it',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d0e59e12-1787244997279.png",
  alt: 'Mobile application interface showing financial tracking dashboard on smartphone screen',
  span: 'large',
  tags: ['Flutter', 'iOS', 'Android']
},
{
  id: 'g6',
  title: 'Customer Support Hub',
  subtitle: 'Omnichannel support for Pinnacle Systems',
  category: 'bpo',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1fa3d9798-1768554124735.png',
  alt: 'Customer support team collaborating at workstations in a modern support center',
  tags: ['Omnichannel', 'Tier 1 & 2']
},
{
  id: 'g7',
  title: 'ERP System Delivery',
  subtitle: 'Custom ERP for manufacturing client — 2 weeks ahead of schedule',
  category: 'it',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cd7df901-1775056957779.png",
  alt: 'Enterprise resource planning software interface showing production and inventory management modules',
  span: 'wide',
  tags: ['ERP', 'Custom Dev']
},
{
  id: 'g8',
  title: 'Live Call Monitoring',
  subtitle: 'Real-time supervisor dashboards for 200-seat center',
  category: 'voip',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b5eb84c5-1787244995667.png",
  alt: 'Call center supervisor monitoring live call queues and agent performance on large display screens',
  tags: ['Live Monitor', 'Analytics']
},
{
  id: 'g9',
  title: 'SEO Content Strategy',
  subtitle: 'Organic traffic tripled in 6 months for Elevate Digital',
  category: 'marketing',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15fbd14c4-1766488541955.png",
  alt: 'SEO analytics showing organic traffic growth charts and keyword ranking improvements',
  tags: ['SEO', 'Content']
},
{
  id: 'g10',
  title: 'Cloud Migration',
  subtitle: 'AWS migration for 12-service microservices architecture',
  category: 'it',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_178fbac79-1772091570606.png',
  alt: 'Cloud infrastructure diagram showing microservices deployed on AWS with monitoring dashboards',
  tags: ['AWS', 'Cloud', 'DevOps']
},
{
  id: 'g11',
  title: 'Lead Generation Funnel',
  subtitle: 'B2B funnel delivering 400+ qualified leads per month',
  category: 'marketing',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_186ddb7d0-1772192912089.png',
  alt: 'Marketing funnel analytics showing lead conversion rates and campaign performance metrics',
  span: 'tall',
  tags: ['Lead Gen', 'B2B']
},
{
  id: 'g12',
  title: 'IVR & Call Routing',
  subtitle: 'Intelligent routing system reducing wait times by 42%',
  category: 'voip',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1c3bedfad-1775833676359.png',
  alt: 'IVR call routing system interface showing call flow diagrams and queue management settings',
  tags: ['IVR', 'ACD']
}];


const categories = [
{ key: 'all', label: 'All Work', icon: Grid3X3 },
{ key: 'it', label: 'IT & Software', icon: Globe },
{ key: 'bpo', label: 'BPO & Call Center', icon: Headphones },
{ key: 'voip', label: 'VOIP Platform', icon: Phone },
{ key: 'marketing', label: 'Digital Marketing', icon: Megaphone }];


const categoryColors: Record<string, {text: string;bg: string;border: string;}> = {
  it: { text: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/30' },
  bpo: { text: 'text-cyan', bg: 'bg-cyan/10', border: 'border-cyan/30' },
  voip: { text: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/30' },
  marketing: { text: 'text-success', bg: 'bg-success/10', border: 'border-success/30' },
  team: { text: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/30' },
  office: { text: 'text-muted-foreground', bg: 'bg-muted', border: 'border-border' }
};

/* ─── Component ──────────────────────────────────────────── */
export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [lightboxItem, setLightboxItem] = useState<GalleryItem | null>(null);

  const filtered = activeCategory === 'all' ?
  galleryItems :
  galleryItems.filter((item) => item.category === activeCategory);

  const getGridClass = (span?: string) => {
    switch (span) {
      case 'wide':return 'col-span-2';
      case 'tall':return 'row-span-2';
      case 'large':return 'col-span-2 row-span-2';
      default:return '';
    }
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="border-b border-border bg-card px-6 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl font-bold text-foreground tracking-tight">Project Gallery</h1>
                <p className="mt-1 text-sm text-muted-foreground max-w-xl">
                  A curated look at the work we've delivered — from enterprise software to global BPO operations and digital campaigns.
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted px-3 py-1.5 rounded-full border border-border">
                <span className="font-semibold text-foreground">{galleryItems.length}</span> projects showcased
              </div>
            </div>

            {/* Category Filters */}
            <div className="mt-6 flex flex-wrap gap-2">
              {categories.map((cat) => {
                const CatIcon = cat.icon;
                const isActive = activeCategory === cat.key;
                return (
                  <button
                    key={cat.key}
                    onClick={() => setActiveCategory(cat.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all duration-200 ${
                    isActive ?
                    'bg-primary text-white border-primary shadow-sm' :
                    'bg-card text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'}`
                    }>

                    <CatIcon size={14} />
                    {cat.label}
                  </button>);

              })}
            </div>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[220px]">
            {filtered.map((item) => {
              const colors = categoryColors[item.category] || categoryColors.office;
              return (
                <div
                  key={item.id}
                  className={`group relative overflow-hidden rounded-xl border border-border bg-card cursor-pointer ${getGridClass(item.span)}`}
                  onClick={() => setLightboxItem(item)}>

                  {/* Image */}
                  <img
                    src={item.image}
                    alt={item.alt}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />


                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                  {/* Hover zoom icon */}
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                      <ZoomIn size={14} className="text-white" />
                    </div>
                  </div>

                  {/* Category badge */}
                  <div className="absolute top-3 left-3">
                    <span className={`text-2xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border backdrop-blur-sm ${colors.bg} ${colors.text} ${colors.border}`}>
                      {item.category === 'it' ? 'IT & Software' : item.category === 'bpo' ? 'BPO' : item.category === 'voip' ? 'VOIP' : item.category === 'marketing' ? 'Marketing' : item.category}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-sm font-semibold text-white leading-snug">{item.title}</h3>
                    <p className="mt-0.5 text-xs text-white/70 line-clamp-1">{item.subtitle}</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {item.tags.slice(0, 3).map((tag) =>
                      <span key={tag} className="text-2xs px-1.5 py-0.5 rounded bg-white/15 text-white/80 backdrop-blur-sm">
                          {tag}
                        </span>
                      )}
                    </div>
                  </div>
                </div>);

            })}
          </div>

          {filtered.length === 0 &&
          <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4">
                <Grid3X3 size={22} className="text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground">No projects in this category yet</p>
              <p className="text-xs text-muted-foreground mt-1">Check back soon or browse all work.</p>
              <button
              onClick={() => setActiveCategory('all')}
              className="mt-4 text-xs text-primary hover:underline">

                View all projects
              </button>
            </div>
          }
        </div>

        {/* Stats strip */}
        <div className="border-t border-border bg-card">
          <div className="max-w-6xl mx-auto px-6 py-6 grid grid-cols-2 sm:grid-cols-4 gap-6">
            {[
            { value: '500+', label: 'Projects Delivered' },
            { value: '120+', label: 'Active Clients' },
            { value: '18', label: 'Countries Served' },
            { value: '96%', label: 'Client Retention' }].
            map((stat) =>
            <div key={stat.label} className="text-center">
                <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{stat.label}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxItem &&
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        onClick={() => setLightboxItem(null)}>

          <div
          className="relative bg-card rounded-2xl overflow-hidden max-w-2xl w-full shadow-2xl border border-border"
          onClick={(e) => e.stopPropagation()}>

            {/* Close */}
            <button
            onClick={() => setLightboxItem(null)}
            className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center border border-white/20 hover:bg-black/60 transition-colors">

              <X size={15} className="text-white" />
            </button>

            {/* Image */}
            <div className="relative h-64 sm:h-80">
              <img
              src={lightboxItem.image}
              alt={lightboxItem.alt}
              className="w-full h-full object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className={`text-2xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border backdrop-blur-sm ${categoryColors[lightboxItem.category]?.bg} ${categoryColors[lightboxItem.category]?.text} ${categoryColors[lightboxItem.category]?.border}`}>
                  {lightboxItem.category === 'it' ? 'IT & Software' : lightboxItem.category === 'bpo' ? 'BPO' : lightboxItem.category === 'voip' ? 'VOIP' : lightboxItem.category === 'marketing' ? 'Marketing' : lightboxItem.category}
                </span>
              </div>
            </div>

            {/* Details */}
            <div className="p-5">
              <h2 className="text-lg font-bold text-foreground">{lightboxItem.title}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{lightboxItem.subtitle}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {lightboxItem.tags.map((tag) =>
              <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-muted border border-border text-muted-foreground">
                    {tag}
                  </span>
              )}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                onClick={() => setLightboxItem(null)}
                className="flex items-center gap-1.5 text-xs text-primary hover:underline">

                  <ExternalLink size={12} />
                  Close preview
                </button>
              </div>
            </div>
          </div>
        </div>
      }
    </AppLayout>);

}